import { AlertCircle, Cloud, Loader2, Plug, ShieldAlert, SquareParking } from 'lucide-react'
import { useConfigForm } from './useSingleConfig'
import { Field, PageHead, PasswordInput, SaveBar, SectionCard, SwitchRow, TextInput } from './ui'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
const str = (v: unknown) => (v == null ? '' : String(v))
const obj = (v: unknown): El => (v && typeof v === 'object' ? (v as El) : {})

type Pmc = { clientId: string; clientSecret: string; username: string; password: string; baseUrl: string; enabled: boolean }
type Form = {
  parkcloud: { apiKey: string; ocpApimSubscriptionKey: string }
  parclick: { apiKey: string; skip: boolean }
  parkimeter: { apiKey: string; skip: boolean }
  parkingmycar: Pmc
  // True only once the backend exposes a PARKINGMYCAR config object — saving it before the
  // backend has a ParkingMyCarConfiguration class makes the ORM adapter throw (HTTP 500).
  pmcSupported: boolean
}

export function ThirdPartyConfigPage() {
  const { query, form, dirty, patch, reset, commit, saving, ok, error } = useConfigForm<Form>(
    'thirdPartyConfiguration',
    (el) => {
      const pc = obj(el.PARKCLOUD)
      const pk = obj(el.PARCLICK)
      const pm = obj(el.PARKIMETER)
      const pmc = obj(el.PARKINGMYCAR)
      return {
        parkcloud: { apiKey: str(pc.apiKey), ocpApimSubscriptionKey: str(pc.ocpApimSubscriptionKey) },
        parclick: { apiKey: str(pk.apiKey), skip: isYes(pk.enableSkipAvailabilityCheckOnUpdate) },
        parkimeter: { apiKey: str(pm.apiKey), skip: isYes(pm.enableSkipAvailabilityCheckOnUpdate) },
        parkingmycar: {
          clientId: str(pmc.clientId),
          clientSecret: str(pmc.clientSecret),
          username: str(pmc.username),
          password: str(pmc.password),
          baseUrl: str(pmc.baseUrl),
          // Absent on legacy rows -> treat as enabled (matches the backend default).
          enabled: pmc.enabled == null ? true : isYes(pmc.enabled),
        },
        pmcSupported: el.PARKINGMYCAR != null && typeof el.PARKINGMYCAR === 'object',
      }
    },
  )

  function save() {
    commit((f) => ({
      PARKCLOUD: { apiKey: f.parkcloud.apiKey, ocpApimSubscriptionKey: f.parkcloud.ocpApimSubscriptionKey },
      PARCLICK: { apiKey: f.parclick.apiKey, enableSkipAvailabilityCheckOnUpdate: f.parclick.skip },
      PARKIMETER: { apiKey: f.parkimeter.apiKey, enableSkipAvailabilityCheckOnUpdate: f.parkimeter.skip },
      // Only persist PARKINGMYCAR once the backend has the matching config class, otherwise the
      // ORM adapter throws on save. The card stays visible (to enter the credentials) regardless.
      ...(f.pmcSupported
        ? {
            PARKINGMYCAR: {
              clientId: f.parkingmycar.clientId,
              clientSecret: f.parkingmycar.clientSecret,
              username: f.parkingmycar.username,
              password: f.parkingmycar.password,
              baseUrl: f.parkingmycar.baseUrl,
              enabled: f.parkingmycar.enabled,
            },
          }
        : {}),
    }))
  }

  const pmc = form?.parkingmycar
  const pmcSupported = form?.pmcSupported ?? false
  const setPmc = (p: Partial<Pmc>) => form && patch({ parkingmycar: { ...form.parkingmycar, ...p } })

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Configurazione · Terze parti"
        description="Credenziali delle integrazioni con i partner di vendita e gli integratori esterni. I valori sono mascherati: usa l’icona occhio per rivelarli."
      />

      {query.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare la configurazione.
        </div>
      ) : !form ? (
        <div className="cv-panel flex items-center justify-center py-20 text-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-2">
            <SectionCard title="ParkCloud" icon={Cloud} desc="Canale di vendita in uscita (Azure API Management).">
              <div className="space-y-4">
                <Field label="Api Key">
                  <PasswordInput
                    value={form.parkcloud.apiKey}
                    disabled={saving}
                    placeholder="—"
                    onChange={(v) => patch({ parkcloud: { ...form.parkcloud, apiKey: v } })}
                  />
                </Field>
                <Field label="Ocp-Apim-Subscription-Key">
                  <PasswordInput
                    value={form.parkcloud.ocpApimSubscriptionKey}
                    disabled={saving}
                    placeholder="—"
                    onChange={(v) => patch({ parkcloud: { ...form.parkcloud, ocpApimSubscriptionKey: v } })}
                  />
                </Field>
              </div>
            </SectionCard>

            <SectionCard title="Parclick" icon={Plug} desc="Integratore di prenotazioni in entrata (SOAP).">
              <div className="space-y-4">
                <Field label="Api Key">
                  <PasswordInput
                    value={form.parclick.apiKey}
                    disabled={saving}
                    placeholder="—"
                    onChange={(v) => patch({ parclick: { ...form.parclick, apiKey: v } })}
                  />
                </Field>
                <SwitchRow
                  title="Salta controllo disponibilità in aggiornamento"
                  desc="Non verifica la disponibilità quando una prenotazione Parclick viene aggiornata."
                  on={form.parclick.skip}
                  disabled={saving}
                  onToggle={(v) => patch({ parclick: { ...form.parclick, skip: v } })}
                />
              </div>
            </SectionCard>

            <SectionCard title="Parkimeter" icon={Plug} desc="Integratore di prenotazioni in entrata (SOAP).">
              <div className="space-y-4">
                <Field label="Api Key">
                  <PasswordInput
                    value={form.parkimeter.apiKey}
                    disabled={saving}
                    placeholder="—"
                    onChange={(v) => patch({ parkimeter: { ...form.parkimeter, apiKey: v } })}
                  />
                </Field>
                <SwitchRow
                  title="Salta controllo disponibilità in aggiornamento"
                  desc="Non verifica la disponibilità quando una prenotazione Parkimeter viene aggiornata."
                  on={form.parkimeter.skip}
                  disabled={saving}
                  onToggle={(v) => patch({ parkimeter: { ...form.parkimeter, skip: v } })}
                />
              </div>
            </SectionCard>

            {pmc && (
              <div className="lg:col-span-2">
                <SectionCard
                  title="ParkingMyCar"
                  icon={SquareParking}
                  desc="Aggregatore parcheggi (REST · autenticazione JWT di sessione)."
                >
                  <div className="space-y-4">
                    <SwitchRow
                      title="Integrazione attiva"
                      desc="Se disattivata, la sincronizzazione ParkingMyCar viene interrotta: nessuna chiamata al loro servizio."
                      on={pmc.enabled}
                      disabled={saving || !pmcSupported}
                      onToggle={(v) => setPmc({ enabled: v })}
                    />
                    {!pmcSupported && (
                      <div className="flex items-start gap-2 rounded-lg border border-[#f0e0b0] bg-oro-soft px-3 py-2 text-[12.5px] text-oro-700">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                          Integrazione in preparazione — il salvataggio si attiverà dopo l’aggiornamento del backend
                          (classe <span className="font-mono">ParkingMyCarConfiguration</span>). Puoi comunque inserire
                          le credenziali qui sotto.
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="Client ID">
                        <PasswordInput value={pmc.clientId} disabled={saving} placeholder="—" onChange={(v) => setPmc({ clientId: v })} />
                      </Field>
                      <Field label="Username">
                        <PasswordInput
                          value={pmc.username}
                          disabled={saving}
                          placeholder="info@garagesanmarco.it"
                          onChange={(v) => setPmc({ username: v })}
                        />
                      </Field>
                      <Field label="Client Secret">
                        <PasswordInput value={pmc.clientSecret} disabled={saving} placeholder="—" onChange={(v) => setPmc({ clientSecret: v })} />
                      </Field>
                      <Field label="Password">
                        <PasswordInput value={pmc.password} disabled={saving} placeholder="—" onChange={(v) => setPmc({ password: v })} />
                      </Field>
                    </div>
                    <Field
                      label="Base URL"
                      hint="Staging: https://www.test1.env.parkingmycar.it · Produzione: https://api.parkingmycar.it"
                    >
                      <TextInput
                        value={pmc.baseUrl}
                        disabled={saving}
                        mono
                        placeholder="https://www.test1.env.parkingmycar.it"
                        onChange={(v) => setPmc({ baseUrl: v })}
                      />
                    </Field>
                  </div>
                </SectionCard>
              </div>
            )}

            <div className="flex items-start gap-3 self-stretch rounded-card border border-[#f0e0b0] bg-oro-soft px-4 py-3.5 text-[13px] text-oro-700 lg:col-span-2">
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                Le chiavi sono credenziali sensibili: condividile solo sui canali sicuri. Lascia un campo invariato per
                conservare il valore esistente.
              </div>
            </div>
          </div>

          <SaveBar dirty={dirty} saving={saving} error={error} ok={ok} onSave={save} onReset={reset} />
        </>
      )}
    </div>
  )
}
